import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import AppBarMenu from "../../components/AppBarMenu";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ResourceList from "../../components/shared/ResourceList";
import ProjectDrawer from "../../components/management/ProjectDrawer";
import ProjectAdvancedFilters from "../../components/management/ProjectAdvancedFilters";
import { api } from "../../services/api/api";

/**
 * Retorna a cor do indicador baseado no progress
 * 0: sem prazo (cinza)
 * 1: atrasado (vermelho)
 * 2: próximo do prazo (amarelo)
 * 3: em dia (verde)
 */
const getProgressColor = (progress) => {
  switch (progress) {
    case 1:
      return "#F44336"; // Vermelho - atrasado
    case 2:
      return "#FFC107"; // Amarelo - próximo do prazo
    case 3:
      return "#4CAF50"; // Verde - em dia
    default:
      return "#9E9E9E"; // Cinza - sem prazo
  }
};

/**
 * Componente que renderiza a etapa com indicador colorido
 */
const StageWithIndicator = ({ stageName, progress }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: getProgressColor(progress),
        flexShrink: 0,
      }}
    />
    <span>{stageName}</span>
  </Box>
);

/**
 * Header customizado para etapa com tooltip de legenda
 */
const StageHeader = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <span>Etapa</span>
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#4CAF50" }} />
            <span style={{ fontSize: "0.75rem" }}>Em dia</span>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#FFC107" }} />
            <span style={{ fontSize: "0.75rem" }}>Próximo do prazo</span>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#F44336" }} />
            <span style={{ fontSize: "0.75rem" }}>Atrasado</span>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#9E9E9E" }} />
            <span style={{ fontSize: "0.75rem" }}>Sem prazo</span>
          </Box>
        </Box>
      }
      arrow
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, color: "action.active", cursor: "help" }} />
    </Tooltip>
  </Box>
);

/**
 * Página dedicada para gestão de projetos
 * Usa ResourceList para listagem paginada e ProjectDrawer para criar/editar
 */
export default function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  
  // Estado do drawer apenas para CRIAR
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createdProjectNumber, setCreatedProjectNumber] = useState(null);
  
  // Estado da página (0-based)
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      const parsed = parseInt(pageParam, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed - 1;
      }
    }
    return 0;
  });
  
  // Sincroniza o estado da página com a URL quando a URL muda
  useEffect(() => {
    const pageParam = searchParams.get('page');
    const urlPage = pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0;
    
    // Sempre sincroniza com a URL
    if (urlPage !== page) {
      setPage(urlPage);
    }
  }, [searchParams]);
  
  // Deriva filtros diretamente dos searchParams (igual ao PlantsPage)
  const currentFilters = useMemo(() => {
    const entries = Array.from(searchParams.entries());
    const filters = {};
    entries.forEach(([key, value]) => {
      if (value && value.trim() !== '' && key !== 'search' && key !== 'page') {
        filters[key] = value;
      }
    });
    return filters;
  }, [searchParams]);

  const handleFiltersChange = useCallback((filters) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams();

      // Adiciona os novos filtros
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, String(value));
        }
      });

      // Compara se os filtros realmente mudaram
      const prevFiltersString = Array.from(prev.entries())
        .filter(([key]) => key !== 'page' && key !== 'search')
        .sort()
        .toString();
      const newFiltersString = Array.from(newParams.entries())
        .sort()
        .toString();

      const filtersChanged = prevFiltersString !== newFiltersString;

      if (filtersChanged) {
        // Se os filtros mudaram, não preserva o page (volta pra primeira)
        setPage(0);
      } else {
        // Se os filtros não mudaram, preserva o page
        const currentPage = prev.get('page');
        if (currentPage) {
          newParams.set('page', currentPage);
        }
      }
      
      // Preserva o search
      const search = prev.get('search');
      if (search) {
        newParams.set('search', search);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  const handlePageChange = useCallback(
    (_event, newPage) => {
      setPage(newPage);
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        if (newPage === 0) {
          params.delete('page');
        } else {
          params.set('page', String(newPage + 1));
        }
        return params;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const handleSearchChange = useCallback((search) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      
      const prevSearch = prev.get('search') || '';
      const searchChanged = prevSearch !== (search || '');
      
      if (search) {
        newParams.set('search', search);
      } else {
        newParams.delete('search');
      }
      
      // Se o search mudou, reseta a página
      if (searchChanged) {
        setPage(0);
        newParams.delete('page');
      }
      
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Função para buscar projetos da API
  const fetchProjects = useCallback(async ({ page, limit, search }) => {
    const params = {
      page,
      limit,
      search: search || undefined,
      ...currentFilters, // Adiciona os filtros avançados
    };
    
    const response = await api.get("/home/projects", { params });
    return response.data;
  }, [currentFilters]);

  // Definição das colunas da tabela - dinâmicas baseadas em filtros
  const columns = useMemo(() => {
    const baseColumns = [
      { 
        header: "Número",
        accessor: "projectNumber",
        align: "left"
      },
      { 
        header: "Nome do Projeto",
        accessor: "projectName",
        align: "left"
      },
      { 
        header: "Integrador",
        accessor: "clientName",
        align: "left"
      },
      { 
        header: <StageHeader />,
        accessor: (item) => <StageWithIndicator stageName={item.stageName} progress={item.progress} />,
        align: "left"
      },
      { 
        header: "Potência (kWp)",
        accessor: (item) => item.power ? `${item.power} kWp` : "-",
        align: "right"
      },
    ];

    // Se há busca por comentário, adiciona coluna de comentários que deram match
    if (currentFilters.commentSearch) {
      baseColumns.push({
        header: "Comentário encontrado",
        accessor: (item) => {
          if (!item.matchedComments || item.matchedComments.length === 0) {
            return "-";
          }
          
          const firstComment = item.matchedComments[0];
          const truncated = firstComment.content.length > 80 
            ? firstComment.content.substring(0, 80) + "..." 
            : firstComment.content;
          
          const tooltipContent = (
            <Box>
              {item.matchedComments.map((comment, idx) => (
                <Box key={comment.id} sx={{ mb: idx < item.matchedComments.length - 1 ? 2 : 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                    {comment.userName} - {new Date(comment.createdAt).toLocaleDateString("pt-BR")}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>
                    {comment.content}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
          
          return (
            <Tooltip title={tooltipContent} arrow placement="top">
              <Typography 
                sx={{ 
                  cursor: "help", 
                  fontSize: "0.875rem",
                  color: "text.secondary",
                  fontStyle: "italic"
                }}
              >
                {truncated}
              </Typography>
            </Tooltip>
          );
        },
        align: "left"
      });
    }

    baseColumns.push({
      header: "Prazo",
      accessor: (item) => {
        if (!item.dueDate) return <span style={{ color: "#999" }}>Sem prazo</span>;
        const [year, month, day] = item.dueDate.substring(0, 10).split("-");
        return `${day}/${month}/${year}`;
      },
      align: "left"
    });

    baseColumns.push({
      header: "Criado em",
      accessor: (item) => {
        if (!item.createdAt) return "-";
        const date = new Date(item.createdAt);
        const relative = formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
        return (
          <Tooltip title={date.toLocaleString("pt-BR")} arrow>
            <span style={{ cursor: "help" }}>
              {date.toLocaleDateString("pt-BR")}
              <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", display: "block", fontStyle: "italic" }}>
                ({relative})
              </Typography>
            </span>
          </Tooltip>
        );
      },
      align: "left"
    });

    return baseColumns;
  }, [currentFilters.commentSearch]);

  const handleCreateProject = () => {
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    // Se criou um projeto, aguarda um pouco para o toast e redireciona
    if (createdProjectNumber) {
      setTimeout(() => {
        navigate(`/projects/${createdProjectNumber}`);
        setCreatedProjectNumber(null);
      }, 1000);
    }
  };

  const handleRowClick = (row) => {
    // Navegar usando o número do projeto ao invés do ID
    navigate(`/projects/${row.projectNumber}`);
  };

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExport = async (format) => {
    handleExportClose();
    
    try {
      const params = {
        ...currentFilters,
        search: searchParams.get('search') || undefined,
        format, // 'excel' ou 'pdf'
      };
      
      const response = await api.get("/home/projects/export", { 
        params,
        responseType: 'blob' // Importante para download de arquivo
      });
      
      // Cria um link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Define o nome do arquivo baseado no formato
      const timestamp = new Date().toISOString().split('T')[0];
      const extension = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `projetos-${timestamp}.${extension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      alert('Erro ao exportar lista. Tente novamente.');
    }
  };

  return (
    <AppBarMenu>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: "text.primary" }}>
            Projetos
          </Typography>
          
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportClick}
              sx={{ 
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1.2,
                fontWeight: 500,
              }}
            >
              Exportar
            </Button>
            
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleExportClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => handleExport('excel')}>
                Exportar para Excel (.xlsx)
              </MenuItem>
              <MenuItem onClick={() => handleExport('pdf')}>
                Exportar para PDF (.pdf)
              </MenuItem>
            </Menu>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateProject}
              sx={{ 
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1.2,
                fontWeight: 500,
              }}
            >
              Novo Projeto
            </Button>
          </Box>
        </Box>

        <ProjectAdvancedFilters 
          onFiltersChange={handleFiltersChange}
          initialFilters={currentFilters}
        />

        <Paper 
          sx={{ 
            borderRadius: "12px", 
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <ResourceList
            fetchData={fetchProjects}
            columns={columns}
            searchParam="search"
            onRowClick={handleRowClick}
            storageKey="projects"
            resourceName="Projetos"
            initialSearch={searchParams.get('search') || ''}
            onSearchChange={handleSearchChange}
            externalPage={page}
            onPageChange={handlePageChange}
          />
        </Paper>

        <ProjectDrawer
          open={drawerOpen}
          onClose={handleCloseDrawer}
          mode="create"
          onProjectCreated={(projectNumber) => setCreatedProjectNumber(projectNumber)}
        />
      </Container>
    </AppBarMenu>
  );
}
